import { useState, useEffect, useMemo, useRef } from 'react'
import { Box, Flex, Text, VStack, Grid, Input, Spinner, useT } from '@minetrace/ui'
import { useFormColors, FormSection } from '@minetrace/ui/forms'
import {
  LuSearch as Search,
  LuX as X,
  LuPlus as Plus,
  LuMountain as Mountain,
} from 'react-icons/lu'
import { AdvancedMarkerAnchorPoint } from '@vis.gl/react-google-maps'
import { KMap, AdvancedMarker, InfoWindow, FitBounds, useMap } from '../../../lib/googleMaps'
import { programacionesService } from '../../../api/services'
import type {
  SupplierOptionResponse,
  SupplierMiningRightResponse,
} from '../../../api/models/response/programacion'

export interface SelectedMiningRight {
  miningRightId: string
  uniqueCode: string
  name: string
  companyId: string
  companyName: string
  companyRuc: string
  miningRightStatusId: number
  geoJson: string | null
  substance: string | null
}

interface Step3Props {
  selectedItems: SelectedMiningRight[]
  onSelectedItemsChange: (items: SelectedMiningRight[]) => void
  /** Contact references per mining right */
  contactReferences?: Map<string, string>
  onContactReferenceChange?: (miningRightId: string, value: string) => void
}

interface ParsedPolygon {
  miningRightId: string
  name: string
  uniqueCode: string
  companyName: string
  miningRightStatusId: number
  geometry: GeoJSON.Geometry
}

const parseGeoJson = (geoJsonStr: string | null): GeoJSON.Geometry | null => {
  if (!geoJsonStr) return null
  try { return JSON.parse(geoJsonStr) as GeoJSON.Geometry } catch { return null }
}

const getStatusTid = (statusId: number): string => {
  switch (statusId) {
    case 1: return 'programaciones.wizard.step3.statusVigente'
    case 2: return 'programaciones.wizard.step3.statusEnTramite'
    case 3: return 'programaciones.wizard.step3.statusExtinto'
    case 4: return 'programaciones.wizard.step3.statusSuspendido'
    default: return 'programaciones.wizard.step3.statusDesconocido'
  }
}

const getStatusColor = (statusId: number): string => {
  switch (statusId) {
    case 1: return '#1d6664'
    case 4: return '#a8740e'
    default: return '#857b71'
  }
}

const getStatusBgColor = (statusId: number): string => {
  switch (statusId) {
    case 1: return 'rgba(22, 163, 74, 0.1)'
    case 4: return 'rgba(202, 138, 4, 0.1)'
    default: return 'rgba(107, 114, 128, 0.1)'
  }
}

type LL = google.maps.LatLngLiteral

/** Extrae los anillos exteriores (lat,lng) de una geometría GeoJSON Polygon/MultiPolygon. */
const geometryRings = (geometry: GeoJSON.Geometry): LL[][] => {
  const rings: LL[][] = []
  if (geometry.type === 'Polygon') {
    for (const ring of geometry.coordinates) {
      rings.push(ring.map(([lng, lat]) => ({ lat, lng })))
    }
  } else if (geometry.type === 'MultiPolygon') {
    for (const poly of geometry.coordinates) {
      for (const ring of poly) {
        rings.push(ring.map(([lng, lat]) => ({ lat, lng })))
      }
    }
  }
  return rings
}

/** Centroide aproximado (centro del bounding box) de una geometría. */
const geometryCenter = (geometry: GeoJSON.Geometry): LL | null => {
  const pts = geometryRings(geometry).flat()
  if (pts.length === 0) return null
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity
  for (const p of pts) {
    if (p.lat < minLat) minLat = p.lat
    if (p.lat > maxLat) maxLat = p.lat
    if (p.lng < minLng) minLng = p.lng
    if (p.lng > maxLng) maxLng = p.lng
  }
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 }
}

/**
 * Polígono imperativo sobre `google.maps.Polygon` (la base no exporta uno).
 * Mismo patrón de ciclo de vida que `lib/googleMaps/Polyline`.
 */
const MapPolygon = ({
  rings,
  color,
  onClick,
}: {
  rings: LL[][]
  color: string
  onClick?: () => void
}) => {
  const map = useMap()
  const polyRef = useRef<google.maps.Polygon | null>(null)

  useEffect(() => {
    if (!map) return
    const poly = new google.maps.Polygon()
    poly.setMap(map)
    polyRef.current = poly
    return () => {
      poly.setMap(null)
      polyRef.current = null
    }
  }, [map])

  useEffect(() => {
    polyRef.current?.setOptions({
      paths: rings,
      strokeColor: color,
      strokeWeight: 2,
      fillColor: color,
      fillOpacity: 0.15,
      clickable: !!onClick,
    })
  }, [rings, color, onClick])

  useEffect(() => {
    const poly = polyRef.current
    if (!poly || !onClick) return
    const listener = poly.addListener('click', onClick)
    return () => listener.remove()
  }, [onClick])

  return null
}

const Step3DerechosMineros = ({ selectedItems, onSelectedItemsChange, contactReferences, onContactReferenceChange }: Step3Props) => {
  const c = useFormColors()
  const { t } = useT()
  const getStatusLabel = (statusId: number): string => t(getStatusTid(statusId))

  const [searchText, setSearchText] = useState('')
  const [searchResults, setSearchResults] = useState<SupplierOptionResponse[]>([])
  const [miningRightsResults, setMiningRightsResults] = useState<Map<string, SupplierMiningRightResponse[]>>(new Map())
  const [loadingSearch, setLoadingSearch] = useState(false)
  const [loadingMR, setLoadingMR] = useState<string | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [expandedSupplier, setExpandedSupplier] = useState<string | null>(null)

  const selectedIds = useMemo(
    () => new Set(selectedItems.map((s) => s.miningRightId)),
    [selectedItems],
  )

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSearchChange = (value: string) => {
    setSearchText(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (value.trim().length < 2) {
      setSearchResults([])
      setShowDropdown(false)
      return
    }
    debounceRef.current = setTimeout(async () => {
      setLoadingSearch(true)
      try {
        const response = await programacionesService.getSuppliers({
          search: value.trim(),
          page: 1,
          pageSize: 5,
        })
        setSearchResults(response.items)
        setShowDropdown(true)
      } catch {
        setSearchResults([])
      } finally {
        setLoadingSearch(false)
      }
    }, 300)
  }

  const handleExpandSupplier = async (supplier: SupplierOptionResponse) => {
    if (expandedSupplier === supplier.companyId) {
      setExpandedSupplier(null)
      return
    }
    setExpandedSupplier(supplier.companyId)

    if (miningRightsResults.has(supplier.companyId)) return

    setLoadingMR(supplier.companyId)
    try {
      const rights = await programacionesService.getSupplierMiningRights(supplier.companyId)
      setMiningRightsResults((prev) => new Map(prev).set(supplier.companyId, rights))
    } catch {
      // silently fail
    } finally {
      setLoadingMR(null)
    }
  }

  const handleAddMiningRight = (mr: SupplierMiningRightResponse, supplier: SupplierOptionResponse) => {
    if (selectedIds.has(mr.miningRightId)) return
    onSelectedItemsChange([
      ...selectedItems,
      {
        miningRightId: mr.miningRightId,
        uniqueCode: mr.uniqueCode,
        name: mr.name,
        companyId: supplier.companyId,
        companyName: supplier.legalName,
        companyRuc: supplier.ruc,
        miningRightStatusId: mr.miningRightStatusId,
        geoJson: mr.geoJson,
        substance: mr.substance,
      },
    ])
  }

  const handleRemoveMiningRight = (miningRightId: string) => {
    onSelectedItemsChange(selectedItems.filter((item) => item.miningRightId !== miningRightId))
  }

  // Map polygons
  const visiblePolygons: ParsedPolygon[] = useMemo(() => {
    return selectedItems
      .map((item) => {
        const geometry = parseGeoJson(item.geoJson)
        if (!geometry) return null
        return {
          miningRightId: item.miningRightId,
          name: item.name,
          uniqueCode: item.uniqueCode,
          companyName: item.companyName,
          miningRightStatusId: item.miningRightStatusId,
          geometry,
        }
      })
      .filter(Boolean) as ParsedPolygon[]
  }, [selectedItems])

  // Geometría lista para Google Maps: anillos por polígono + centroide del tooltip.
  const mapPolygons = useMemo(
    () =>
      visiblePolygons
        .map((p) => {
          const rings = geometryRings(p.geometry)
          if (rings.length === 0) return null
          return { ...p, rings, center: geometryCenter(p.geometry) }
        })
        .filter(Boolean) as (ParsedPolygon & { rings: LL[][]; center: LL | null })[],
    [visiblePolygons],
  )

  const mapBoundsPoints = useMemo<LL[]>(
    () => mapPolygons.flatMap((p) => p.rings.flat()),
    [mapPolygons],
  )

  // Tooltip de derecho minero (hover sobre el polígono).
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  return (
    <FormSection
      icon={Mountain}
      titleTid="programaciones.wizard.step3.sectionTitle"
      descriptionTid="programaciones.wizard.step3.sectionDescription"
    >
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5}>
        {/* Left: Search + Selected cards */}
        <VStack gap={4} alignItems="stretch">
          {/* Search bar with dropdown */}
          <Box position="relative" ref={dropdownRef}>
            <Flex gap={2}>
              <Input
                value={searchText}
                onChange={(e) => handleSearchChange(e.target.value.slice(0, 200))}
                placeholder={t('programaciones.wizard.step3.searchPlaceholder')}
                maxLength={200}
                bg={c.bgInput}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                color={c.text}
                h="44px"
                fontSize="sm"
                _placeholder={{ color: c.textMuted }}
                _focus={{ outline: 'none', borderColor: '#b4532a', ring: '1px', ringColor: '#b4532a' }}
                onFocus={() => { if (searchResults.length > 0) setShowDropdown(true) }}
              />
              <Flex alignItems="center" justifyContent="center" px={3} h="44px" bg="#b4532a" color="white" borderRadius="lg" flexShrink={0}>
                {loadingSearch ? <Spinner size="sm" color="white" /> : <Search size={16} />}
              </Flex>
            </Flex>

            {/* Dropdown results */}
            {showDropdown && searchResults.length > 0 && (
              <Box
                position="absolute"
                top="50px"
                left={0}
                right={0}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                shadow="lg"
                zIndex={100}
                maxH="400px"
                overflowY="auto"
                py={2}
              >
                {searchResults.map((supplier) => {
                  const rights = miningRightsResults.get(supplier.companyId) ?? []
                  const isExpanded = expandedSupplier === supplier.companyId
                  const isLoadingThis = loadingMR === supplier.companyId

                  return (
                    <Box key={supplier.companyId}>
                      {/* Supplier header */}
                      <Flex
                        px={4}
                        py={3}
                        alignItems="center"
                        gap={3}
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => handleExpandSupplier(supplier)}
                      >
                        <Flex
                          w="32px" h="32px" borderRadius="md" bg="#f1f5f9"
                          alignItems="center" justifyContent="center" flexShrink={0}
                        >
                          <Text fontSize="sm" color="#64748b">🏢</Text>
                        </Flex>
                        <Box flex={1} minW={0}>
                          <Text fontSize="sm" fontWeight="700" color="#1e293b" truncate>
                            {supplier.legalName}
                          </Text>
                          <Text tid="programaciones.wizard.step3.rucLabel" values={{ ruc: supplier.ruc }} fontSize="xs" color="#64748b" fontFamily="monospace" />
                        </Box>
                        {isLoadingThis && <Spinner size="xs" color="#1d6664" />}
                      </Flex>

                      {/* Mining rights list (expandable) */}
                      {isExpanded && (
                        <Box pl={12} pr={4} pb={2}>
                          {isLoadingThis ? (
                            <Flex py={2} justifyContent="center">
                              <Spinner size="sm" color="#1d6664" />
                            </Flex>
                          ) : rights.length === 0 ? (
                            <Text tid="programaciones.wizard.step3.noMiningRights" fontSize="xs" color="#94a3b8" py={2} />
                          ) : (
                            <VStack gap={1} alignItems="stretch">
                              {rights.map((mr) => {
                                const alreadyAdded = selectedIds.has(mr.miningRightId)
                                return (
                                  <Flex
                                    key={mr.miningRightId}
                                    p={3}
                                    bg={alreadyAdded ? '#f0faf4' : 'white'}
                                    border="1px solid"
                                    borderColor={alreadyAdded ? '#bfe3df' : '#e8dfd1'}
                                    borderRadius="lg"
                                    alignItems="center"
                                    gap={3}
                                    transition="all 0.15s"
                                    _hover={!alreadyAdded ? { borderColor: '#b4532a', bg: '#fafafa' } : undefined}
                                  >
                                    <Box flex={1} minW={0}>
                                      <Text fontSize="sm" fontWeight="600" color="#1e293b" truncate>
                                        {mr.name}
                                      </Text>
                                      <Flex gap={2} alignItems="center" mt={0.5}>
                                        <Text tid="programaciones.wizard.step3.codeLabel" values={{ code: mr.uniqueCode }} fontSize="xs" color="#64748b" fontFamily="monospace" />
                                        {mr.substance && (
                                          <Text fontSize="xs" color="#94a3b8">
                                            · {mr.substance}
                                          </Text>
                                        )}
                                      </Flex>
                                    </Box>
                                    <Box
                                      px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="bold"
                                      bg={getStatusBgColor(mr.miningRightStatusId)}
                                      color={getStatusColor(mr.miningRightStatusId)}
                                    >
                                      {getStatusLabel(mr.miningRightStatusId)}
                                    </Box>
                                    {!alreadyAdded ? (
                                      <Flex
                                        w="28px" h="28px" borderRadius="full"
                                        bg="#b4532a" alignItems="center" justifyContent="center"
                                        cursor="pointer" _hover={{ bg: '#085a2e' }}
                                        flexShrink={0}
                                        onClick={(e) => { e.stopPropagation(); handleAddMiningRight(mr, supplier) }}
                                      >
                                        <Plus size={14} color="white" />
                                      </Flex>
                                    ) : (
                                      <Text tid="programaciones.wizard.step3.added" fontSize="xs" color="#1d6664" fontWeight="600" />
                                    )}
                                  </Flex>
                                )
                              })}
                            </VStack>
                          )}
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>

          {/* Selected mining rights as cards */}
          {selectedItems.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="600" color="#1d6664" mb={2}>
                {selectedItems.length === 1
                  ? t('programaciones.wizard.step3.selectedCount', { count: selectedItems.length })
                  : t('programaciones.wizard.step3.selectedCountPlural', { count: selectedItems.length })}
              </Text>
              <VStack gap={2} alignItems="stretch">
                {selectedItems.map((item) => (
                  <Flex
                    key={item.miningRightId}
                    bg="#f0faf4"
                    border="1px solid"
                    borderColor="#1d6664"
                    borderRadius="xl"
                    p={3}
                    alignItems="center"
                    gap={3}
                  >
                    <Box flex={1} minW={0}>
                      <Flex gap={2} alignItems="center">
                        <Text fontSize="sm" fontWeight="700" color="#1e293b" truncate>
                          {item.name}
                        </Text>
                        <Box
                          px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="bold"
                          bg={getStatusBgColor(item.miningRightStatusId)}
                          color={getStatusColor(item.miningRightStatusId)}
                        >
                          {getStatusLabel(item.miningRightStatusId)}
                        </Box>
                      </Flex>
                      <Text fontSize="xs" color="#64748b" fontFamily="monospace" mt={0.5}>
                        {item.uniqueCode} {item.substance ? `· ${item.substance}` : ''}
                      </Text>
                      <Flex gap={1} alignItems="center" mt={1}>
                        <Text fontSize="xs" color="#94a3b8">
                          {item.companyName}
                        </Text>
                        <Text fontSize="xs" color="#cbd5e1">·</Text>
                        <Text fontSize="xs" color="#94a3b8" fontFamily="monospace">
                          {item.companyRuc}
                        </Text>
                      </Flex>
                      <Input
                        mt={2}
                        size="sm"
                        placeholder={t('programaciones.wizard.step3.contactReferencePlaceholder')}
                        value={contactReferences?.get(item.miningRightId) ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onContactReferenceChange?.(item.miningRightId, e.target.value.slice(0, 500))}
                        maxLength={500}
                        bg="white"
                        borderColor="#d3c6b2"
                        fontSize="xs"
                        _placeholder={{ color: '#cbd5e1' }}
                      />
                      <Text fontSize="xs" color="#94a3b8" textAlign="right" fontVariantNumeric="tabular-nums">
                        {(contactReferences?.get(item.miningRightId) ?? '').length}/500
                      </Text>
                    </Box>
                    <Flex
                      w="28px" h="28px" borderRadius="full"
                      bg="red.50" alignItems="center" justifyContent="center"
                      cursor="pointer" _hover={{ bg: 'red.100' }}
                      onClick={() => handleRemoveMiningRight(item.miningRightId)}
                      flexShrink={0}
                    >
                      <X size={14} color="#e53e3e" />
                    </Flex>
                  </Flex>
                ))}
              </VStack>
            </Box>
          )}

          {selectedItems.length === 0 && (
            <Box py={8} textAlign="center">
              <Text tid="programaciones.wizard.step3.emptyHint" fontSize="sm" color="#94a3b8" />
            </Box>
          )}
        </VStack>

        {/* Right: Map */}
        <Box
          borderRadius="xl"
          overflow="hidden"
          border="1px solid #e8dfd1"
          h={{ base: '350px', lg: '100%' }}
          minH="450px"
          position="relative"
        >
          <Box
            position="absolute" top={3} right={3} zIndex={1000}
            bg="white" px={3} py={1} borderRadius="md" shadow="sm"
          >
            <Text tid="programaciones.wizard.step3.mapTitle" fontSize="sm" fontWeight="700" color="#2a2622" />
          </Box>
          <KMap defaultCenter={{ lat: -12.0464, lng: -77.0428 }} defaultZoom={6} mapTypeId="hybrid">
            {mapPolygons.map((p) => {
              const color = getStatusColor(p.miningRightStatusId)
              return (
                <MapPolygon
                  key={p.miningRightId}
                  rings={p.rings}
                  color={color}
                  onClick={() => setHoveredId((cur) => (cur === p.miningRightId ? null : p.miningRightId))}
                />
              )
            })}
            {/* Marcador invisible centrado para captar hover y anclar el tooltip */}
            {mapPolygons.map((p) =>
              p.center ? (
                <AdvancedMarker
                  key={`mk-${p.miningRightId}`}
                  position={p.center}
                  anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
                  onMouseEnter={() => setHoveredId(p.miningRightId)}
                  onMouseLeave={() => setHoveredId((cur) => (cur === p.miningRightId ? null : cur))}
                >
                  <div style={{ width: 14, height: 14 }} />
                </AdvancedMarker>
              ) : null,
            )}
            {/* Tooltip del derecho minero bajo el cursor */}
            {(() => {
              const p = mapPolygons.find((x) => x.miningRightId === hoveredId)
              if (!p || !p.center) return null
              return (
                <InfoWindow position={p.center} pixelOffset={[0, -10]} disableAutoPan headerDisabled>
                  <Box p={1}>
                    <Text fontSize="xs" fontWeight="700">{p.name}</Text>
                    <Text fontSize="xs" color="#4a5568">{p.companyName}</Text>
                    <Flex gap={2} mt={1} alignItems="center">
                      <Box w="8px" h="8px" borderRadius="full" bg={getStatusColor(p.miningRightStatusId)} />
                      <Text fontSize="xs">{getStatusLabel(p.miningRightStatusId)}</Text>
                    </Flex>
                  </Box>
                </InfoWindow>
              )
            })()}
            <FitBounds points={mapBoundsPoints} padding={30} maxZoom={14} />
          </KMap>
        </Box>
      </Grid>
    </FormSection>
  )
}

export default Step3DerechosMineros
